// One claim line, opened beside the run as a side panel: what it is, its
// status, the fields a person corrects by hand, and removing it.
//
// Replaced the line-edit dialog and the status picker in the table
// (2026-10-20). A status change and a removal each ask here, in the panel,
// before anything is written; the writes are the page's, through
// lib/billing/runs.js.

import { useEffect, useState } from 'react';
import { lineStatusQuestion } from '../../../lib/billing/runs';
import { LINE_STATUS_COLOR, DENIAL_CODES, fmt$2 } from './format';

const formOf = (l) => ({
  paid_amount: l.paid_amount || '', allowed_amount: l.allowed_amount || '', icn: l.icn || '',
  paid_date: l.paid_date || '', denial_code: l.denial_code || '', denial_reason: l.denial_reason || '',
  prior_auth_number: l.prior_auth_number || '', notes: l.notes || '',
});

export default function LinePanel({ line, run, onClose, onSave, onChangeStatus, onRemove }) {
  const [form, setForm] = useState(() => formOf(line));
  const [status, setStatus] = useState(line.status);
  const [confirm, setConfirm] = useState(null);   // 'status' | 'remove' | null
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [saved, setSaved] = useState('');

  useEffect(() => { setForm(formOf(line)); setStatus(line.status); setConfirm(null); setError(''); setSaved(''); }, [line.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const run_ = async (fn, done) => {
    setBusy(true); setError(''); setSaved('');
    try { await fn(); setSaved(done); setConfirm(null); }
    catch (err) { setError(err.message); }
    finally { setBusy(false); }
  };
  const set = (k) => (e) => setForm(p => ({ ...p, [k]: e.target.value }));
  const field = (k, label, props = {}) => (
    <div className="form-group">
      <label className="form-label" htmlFor={`line-${k}`}>{label}</label>
      <input id={`line-${k}`} className="form-input" value={form[k]} onChange={set(k)} {...props} />
    </div>
  );
  const lc = LINE_STATUS_COLOR[line.status] || LINE_STATUS_COLOR.pending;

  return (
    <div className="modal-overlay drawer" onMouseDown={e => e.target === e.currentTarget && !busy && onClose()}>
      <div className="modal" role="dialog" aria-label={`Claim line, ${line.client_name || ''} ${line.service_date || ''}`} onMouseDown={e => e.stopPropagation()}>
        <div className="modal-header">
          <div>
            <div className="modal-title">{line.client_name || 'Claim line'}</div>
            <div style={{ fontSize: 12, color: 'var(--text3)', marginTop: 2 }}>
              {line.service_date} · <span className="mono">{line.service_code}{line.modifier ? ` ${line.modifier}` : ''}</span> · {line.hours}h
            </div>
          </div>
          <button className="btn btn-ghost btn-sm" onClick={onClose} disabled={busy} aria-label="Close">✕</button>
        </div>
        <div className="modal-body" style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <dl className="line-facts">
            <div><dt>Medicaid ID</dt><dd className="mono">{line.medicaid_id || '—'}</dd></div>
            <div><dt>Accrued</dt><dd>{fmt$2(line.accrued_amount)}</dd></div>
            <div><dt>Charge</dt><dd>{fmt$2(line.charge_amount)}</dd></div>
            <div><dt>Hours basis</dt><dd>{line.hours_basis || '—'}{line.hours_basis === 'clocked_else_scheduled' ? ` (clocked ${line.clocked_hours ?? 0}h, scheduled ${line.scheduled_hours ?? '—'}h)` : ''}</dd></div>
            <div><dt>AxisCare visit</dt><dd className="mono">{line.axiscare_visit_id || '—'}</dd></div>
            <div><dt>Diagnosis</dt><dd>{line.diagnosis_code || '—'}</dd></div>
          </dl>

          <section aria-label="Status" className="line-section">
            <div className="line-section-head">Status <span className="run-chip" style={{ background: lc.bg, color: lc.color }}>{line.status}</span></div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
              <label className="sr-only" htmlFor="line-status">New status</label>
              <select id="line-status" className="form-select" value={status} onChange={e => { setStatus(e.target.value); setConfirm(e.target.value !== line.status ? 'status' : null); }} style={{ maxWidth: 200 }}>
                {Object.keys(LINE_STATUS_COLOR).map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            {confirm === 'status' && (
              <div className="run-note warn" role="alert">
                <div style={{ whiteSpace: 'pre-line' }}>{lineStatusQuestion({ line, run, status })}</div>
                <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                  <button className="btn btn-primary btn-sm" disabled={busy} onClick={() => run_(() => onChangeStatus(status), `Status changed to ${status}.`)}>Change status</button>
                  <button className="btn btn-outline btn-sm" disabled={busy} onClick={() => { setStatus(line.status); setConfirm(null); }}>Cancel</button>
                </div>
              </div>
            )}
          </section>

          <section aria-label="Corrections" className="line-section">
            <div className="line-section-head">Payment and corrections</div>
            <div className="form-row">{field('prior_auth_number', 'Prior auth number', { placeholder: 'PA-…' })}{field('icn', 'ICN', { placeholder: 'Medicaid ICN' })}</div>
            <div className="form-row">{field('allowed_amount', 'Allowed amount ($)', { type: 'number', step: '0.01', placeholder: '0.00' })}{field('paid_amount', 'Paid amount ($)', { type: 'number', step: '0.01', placeholder: '0.00' })}</div>
            <div className="form-row">
              {field('paid_date', 'Paid date', { type: 'date' })}
              <div className="form-group">
                <label className="form-label" htmlFor="line-denial_code">Denial code</label>
                <select id="line-denial_code" className="form-select" value={form.denial_code} onChange={set('denial_code')}>
                  <option value="">—</option>
                  {DENIAL_CODES.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
            </div>
            {field('denial_reason', 'Denial reason', { placeholder: 'Notes…' })}
            <div className="form-group">
              <label className="form-label" htmlFor="line-notes">Notes</label>
              <textarea id="line-notes" className="form-textarea" rows={2} value={form.notes} onChange={set('notes')} />
            </div>
            <div><button className="btn btn-primary btn-sm" disabled={busy} onClick={() => run_(() => onSave(form), 'Saved.')}>Save changes</button></div>
          </section>

          {saved && <div className="run-note ok" role="status">{saved}</div>}
          {error && <div className="run-note danger" role="alert">{error}</div>}

          {run.status === 'approved' && (
            <section aria-label="Remove" className="line-section">
              {confirm !== 'remove' ? (
                <div><button className="btn btn-outline btn-sm" style={{ color: 'var(--danger)', borderColor: 'var(--danger)' }} onClick={() => setConfirm('remove')}>Remove this line…</button></div>
              ) : (
                <div className="run-note danger" role="alert">
                  Remove {line.client_name || 'this line'} on {line.service_date} from the run? This cannot be undone.
                  <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                    <button className="btn btn-danger btn-sm" style={{ background: 'var(--danger)', color: 'var(--card-bg)', border: 'none' }} disabled={busy}
                      onClick={() => run_(onRemove, 'Removed.')}>Remove line</button>
                    <button className="btn btn-outline btn-sm" disabled={busy} onClick={() => setConfirm(null)}>Cancel</button>
                  </div>
                </div>
              )}
            </section>
          )}
        </div>
      </div>
    </div>
  );
}
